import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axios";

export const createEstimate = createAsyncThunk(
  "estimate/createEstimate",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance.post('/estimates', {
        name: state.currentEstimate.customerData.name ?? null,
        email: state.currentEstimate.customerData.email ?? null,
        clinic: state.currentEstimate.clinicData.clinicNo,
        financingOption: state.currentEstimate.customerData.financingOption ?? null,
        downPayment: state.currentEstimate.customerData.downPayment ?? null,
    });

    return response.data;
  }
);

export const addProduct = createAsyncThunk(
  "estimate/addProduct",
  async ({productId, quantity}, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance
      .post(`/estimates/${state.currentEstimate.id}/products`, {
          productId: productId,
          quantity: quantity,
          clinic: state.currentEstimate.clinicData.clinicNo,
          province: state.currentEstimate.clinicData.province,
          affinity: state.currentEstimate.customerData.affinityProgram,
          financingOption: state.currentEstimate.customerData.financingOption,
          downPayment: state.currentEstimate.customerData.downPayment,
          marketingDiscounts: state.currentEstimate.customerData.marketingDiscounts,
          programEligability: state.currentEstimate.customerData.programEligability,
          programDiscount: state.currentEstimate.customerData.programDiscount,
      });

    return response.data;
  }
);

export const updateDiscounts = createAsyncThunk(
  "estimate/updateDiscounts",
  async (discounts, thunkAPI) => {
    const state = thunkAPI.getState();

    const params = {}

    params.province = state.currentEstimate.clinicData.province;
    params.clinic = state.currentEstimate.clinicData.clinicNo;

    if (discounts.marketingDiscounts !== undefined) {
      params.marketingDiscounts = discounts.marketingDiscounts;
    }

    if (discounts.affinityProgram !== undefined) {
      params.affinity = discounts.affinityProgram;
    }

    if (discounts.financingOption !== undefined) {
      params.financingOption = discounts.financingOption;
    }

    if (discounts.downPayment !== undefined) {
      params.downPayment = discounts.downPayment;
    }

    if (discounts.programEligability !== undefined) {
      params.programEligability = discounts.programEligability;
    }

    if (discounts.programDiscount !== undefined) {
      params.programDiscount = discounts.programDiscount;
    }

    if (params.length === 0) {
      return null;
    }

    const reponse = await axiosInstance
      .post(`/estimates/${state.currentEstimate.id}/discounts`, params)
      
    return reponse.data;
  }
)

export const removeProduct = createAsyncThunk(
  "estimate/removeProduct",
  async (productId, thunkAPI) => {
    const state = thunkAPI.getState();
    
    const response = await axiosInstance
      .delete(`/estimates/${state.currentEstimate.id}/products/${productId}`, {
          data: {
            clinic: state.currentEstimate.clinicData.clinicNo,
            province: state.currentEstimate.clinicData.province,
            affinity: state.currentEstimate.customerData.affinityProgram,
            financingOption: state.currentEstimate.customerData.financingOption,
            downPayment: state.currentEstimate.customerData.downPayment,
            marketingDiscounts: state.currentEstimate.customerData.marketingDiscounts,
            programEligability: state.currentEstimate.customerData.programEligability,
            programDiscount: state.currentEstimate.customerData.programDiscount,
          }
      });
    
    return response.data;
  }
)

export const updateProduct = createAsyncThunk(
  "estimate/updateProduct",
  async ({productId, newProductId, quantity, recommended}, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance
      .put(`/estimates/${state.currentEstimate.id}/products/${productId}`, {
        productId: newProductId,
        quantity: quantity,
        recommended: recommended,
        clinic: state.currentEstimate.clinicData.clinicNo,
        province: state.currentEstimate.clinicData.province,
        affinity: state.currentEstimate.customerData.affinityProgram,
        financingOption: state.currentEstimate.customerData.financingOption,
        downPayment: state.currentEstimate.customerData.downPayment,
        marketingDiscounts: state.currentEstimate.customerData.marketingDiscounts,
        programEligability: state.currentEstimate.customerData.programEligability,
        programDiscount: state.currentEstimate.customerData.programDiscount,
      })

    return response.data;
  }
)

export const fetchEstimate = createAsyncThunk(
  "estimate/fetchEstimate",
  async (estimateId, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance
      .get(`/estimates/${estimateId}`, {
        params: {
          province: state.currentEstimate.clinicData.province
        }
      })

    return response.data;
  }
)

export const saveEstimate = createAsyncThunk(
  "estimate/saveEstimate",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance
      .post(`/estimates/${state.currentEstimate.id}/save`)
    
    return response.data;
  }
)

export const updateEstimate = createAsyncThunk(
  "estimate/updateEstimate",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance.put(`/estimates/${state.currentEstimate.id}`, {
        name: state.currentEstimate.customerData.name ?? null,
        email: state.currentEstimate.customerData.email ?? null,
        clinic: state.currentEstimate.clinicData.clinicNo,
    });

    return response.data;
  }
);

export const loadTemplateEstimate = createAsyncThunk(
  "estimate/loadTemplateEstimate",
  async (templateId, thunkAPI) => {
    const state = thunkAPI.getState();

    const response = await axiosInstance
      .put(`/templates/${templateId}`, {
          estimateId: state.currentEstimate.id,
          province: state.currentEstimate.clinicData.province,
          affinity: state.currentEstimate.customerData.affinityProgram,
          marketingDiscounts: state.currentEstimate.customerData.marketingDiscounts,
          clinic: state.currentEstimate.clinicData.clinicNo,
          programEligability: state.currentEstimate.customerData.programEligability,
          programDiscount: state.currentEstimate.customerData.programDiscount,
          financingOption: state.currentEstimate.customerData.financingOption,
          downPayment: state.currentEstimate.customerData.downPayment,
      });

    return response.data;
  }
);

const currentEstimateSlice = createSlice({
  name: "estimate",
  initialState: {
    id: null,
    created_by: null,
    created_date: null,
    finalized: null,
    customerData: {
      name: null,
      email: null,
      programEligability: false,
      programDiscount: null,
      marketingDiscounts: null,
      affinityProgram: null,
      financingOption: null,
      downPayment: null,
    },
    clinicData: {
      clinicNo: null,
      province: null,
    },
    products: [],
    status: "idle",
    discountsUpdating: false,
    error: null,
  },
  reducers: {
    newEstimate(state, action) {
      state.id = null;
      state.created_by = null;
      state.created_date = null;
      state.customerData.name = null;
      state.customerData.email = null;
      state.customerData.programEligability = false;
      state.customerData.programDiscount = null;
      state.customerData.marketingDiscounts = null;
      state.customerData.affinityProgram = null;
      state.customerData.financingOption = null;
      state.customerData.downPayment = null;
      state.products = [];
      state.finalized = null;
      state.status = "idle";
    },
    setCustomerData(state, action) {
      state.customerData.name = action.payload.name;
      state.customerData.email = action.payload.email;
      state.customerData.programEligability = action.payload.programEligability;
      state.customerData.programDiscount = action.payload.programDiscount;
      state.customerData.marketingDiscounts = action.payload.marketingDiscounts;
      state.customerData.affinityProgram = action.payload.affinityProgram;
      state.customerData.financingOption = action.payload.financingOption;
      state.customerData.downPayment = action.payload.downPayment;
    },
    setClinicData(state, action) {
      state.clinicData.clinicNo = action.payload.clinicNo;
      state.clinicData.province = action.payload.province;
    },
    setProducts(state, action) {
      state.products = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload;
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateDiscounts.pending, (state) => {
        state.status = "loading";
        state.discountsUpdating = true;
      })
      .addCase(updateDiscounts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.discountsUpdating = false;
        state.products = action.payload;
      })
      .addCase(updateDiscounts.rejected, (state, action) => {
        state.status = "failed";
        state.discountsUpdating = false;
        state.error = action.error.message;
      })
      .addCase(createEstimate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createEstimate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.id = action.payload.estimate_id;
        state.created_by = action.payload.created_by;
        state.created_date = action.payload.created_date;
        state.customerData.name = action.payload.client_name;
        state.customerData.email = action.payload.client_email;
        state.finalized = action.payload.finalized;
      })
      .addCase(createEstimate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(removeProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload;

        if (state.customerData.marketingDiscounts) {
          const productIds = new Set(state.products.map(p => p.id));
          Object.keys(state.customerData.marketingDiscounts).forEach(discountId => {
            const discountProducts = state.customerData.marketingDiscounts[discountId];
            if (discountProducts !== -1) {
              Object.keys(discountProducts).forEach(productId => {
                if (!productIds.has(Number(productId))) {
                  delete discountProducts[productId];
                }
              });

            if (discountProducts !== -1 && Object.keys(discountProducts).length === 0) {
              delete state.customerData.marketingDiscounts[discountId];
            }
          }});
        }
      })
      .addCase(removeProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchEstimate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchEstimate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.id = action.payload.estimate_id;
        state.created_by = action.payload.created_by;
        state.created_date = action.payload.created_date;
        state.customerData.name = action.payload.client_name;
        state.customerData.email = action.payload.client_email;
        state.products = action.payload.products;
        state.finalized = action.payload.finalized;
        state.clinicData.clinicNo = action.payload.clinic_no;
        state.clinicData.province = action.payload.province;
        state.customerData.programEligability = action.payload.program_eligibility;
        state.customerData.programDiscount = action.payload.program_discount;
        state.customerData.affinityProgram = action.payload.affinity_program;
        state.customerData.financingOption = action.payload.financing_option;
        state.customerData.downPayment = action.payload.down_payment;
        state.customerData.marketingDiscounts = action.payload.marketing_discounts_list;
      })
      .addCase(fetchEstimate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(saveEstimate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveEstimate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.finalized = action.payload.finalized;
      })
      .addCase(saveEstimate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateEstimate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateEstimate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.id = action.payload.estimate_id;
        state.customerData.name = action.payload.client_name;
        state.customerData.email = action.payload.client_email;
      })
      .addCase(updateEstimate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(loadTemplateEstimate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadTemplateEstimate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.created_by = action.payload.created_by;
        state.created_date = action.payload.created_date;
        state.customerData.name = action.payload.client_name;
        state.customerData.email = action.payload.client_email;
        state.products = action.payload.products;
        state.finalized = action.payload.finalized;
        state.clinicData.clinicNo = action.payload.clinic_no;
        state.clinicData.province = action.payload.province;
        state.customerData.programEligability = action.payload.program_eligibility;
        state.customerData.programDiscount = action.payload.program_discount;
        state.customerData.affinityProgram = action.payload.affinity_program;
        state.customerData.financingOption = action.payload.financing_option;
        state.customerData.downPayment = action.payload.down_payment;
        state.customerData.marketingDiscounts = action.payload.marketing_discounts_list;
      })
      .addCase(loadTemplateEstimate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  }
});

export const { newEstimate, setCustomerData, setClinicData, setProducts } = currentEstimateSlice.actions;

export default currentEstimateSlice.reducer;
